import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import * as Sentry from '@sentry/gatsby'
import classNames from 'classnames'
import { getImage } from 'gatsby-plugin-image'
import {
  ContentfulRichTextGatsbyReference,
  renderRichText,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text'
import React from 'react'

import { APIEmployee } from '../../backend'
import { LargeLeadForm } from '../components/blog/LargeLeadForm'
import { SmallLeadForm } from '../components/blog/SmallLeadForm'
import { Table } from '../components/blog/Table'
import { TableOfContents } from '../components/blog/TableOfContents'
import { ContentfulLink } from '../components/ContentfulLink'
import { LandingSection } from '../components/landing/LandingSection'
import MeasurImage from '../components/MeasurImage'
import { PlainProduct } from '../hooks/useProducts'
import { isHyphenatedLocale, MeasurLocale } from '../locale/i18n'

import { toDashedSlug, renderFormulas } from './formatting'
import {
  nodeMappings,
  RichTextAssetReferences,
  RichTextEntryLinkReference,
  RichTextEntryReference,
} from './richText'
import { GatsbyImage, TeamMemberPageLink } from './shared-types'

type LocalisedContentfulNode = { node_locale: MeasurLocale }

export const removeWhitespace = (s: string) => s.replace(/\s/g, '')

export const filterLocale = <A extends LocalisedContentfulNode>(
  nodeArr: A[],
  locale: MeasurLocale,
): A[] => nodeArr.filter(i => i.node_locale === locale)

export const renderRichTextWithHTML = ({
  text,
  pageSlug,
  products,
  employees,
  teamMemberPageLinks,
}: {
  text: RenderRichTextData<
    | RichTextAssetReferences
    | RichTextEntryLinkReference
    | RichTextEntryReference
  >
  pageSlug?: string
  products?: PlainProduct[]
  employees?: APIEmployee[]
  teamMemberPageLinks?: TeamMemberPageLink[]
}) => {
  try {
    return renderRichText(text, {
      renderMark: {
        [MARKS.ITALIC]: node => <em key={node?.toString()}>{node}</em>,
        [MARKS.BOLD]: node => <strong key={node?.toString()}>{node}</strong>,
      },
      renderNode: {
        // h1 reserved for page titles
        [BLOCKS.HEADING_2]: (_, children) => (
          <h2
            className={classNames(
              'richText__heading',
              'richText__heading--h2',
              {
                hyphenate: isHyphenatedLocale(),
              },
            )}
          >
            {children}
          </h2>
        ),
        [BLOCKS.HEADING_3]: (_, children) => (
          <h3
            className={classNames(
              'richText__heading',
              'richText__heading--h3',
              {
                hyphenate: isHyphenatedLocale(),
              },
            )}
          >
            {children}
          </h3>
        ),
        [BLOCKS.HEADING_4]: (_, children) => (
          <h4
            className={classNames(
              'richText__heading',
              'richText__heading--h4',
              {
                hyphenate: isHyphenatedLocale(),
              },
            )}
          >
            {children}
          </h4>
        ),
        [BLOCKS.HEADING_5]: (_, children) => (
          <h5
            className={classNames(
              'richText__heading',
              'richText__heading--h5',
              {
                hyphenate: isHyphenatedLocale(),
              },
            )}
          >
            {children}
          </h5>
        ),
        [BLOCKS.HEADING_6]: (_, children) => (
          <h6
            className={classNames(
              'richText__heading',
              'richText__heading--h6',
              {
                hyphenate: isHyphenatedLocale(),
              },
            )}
          >
            {children}
          </h6>
        ),
        [BLOCKS.UL_LIST]: (_, children) =>
          nodeMappings.bulletList({ children }),
        [BLOCKS.OL_LIST]: (_, children) =>
          nodeMappings.orderedList({ children }),
        [BLOCKS.LIST_ITEM]: (_, children) =>
          nodeMappings.listItem({ children }),
        [BLOCKS.PARAGRAPH]: (_, children) =>
          nodeMappings.paragraph({ children }),
        [BLOCKS.TABLE]: (_, children) => (
          <div className="richText__tableScrollWrapper">
            <table className="richText__table">
              <tbody>{children}</tbody>
            </table>
          </div>
        ),
        [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
          if (node.data.target === undefined) {
            return children
          }
          const target = node.data.target as GatsbyImage
          const gatsbyImage =
            target.gatsbyImageData && getImage(target.gatsbyImageData)
          if (
            target.contentful_id === undefined ||
            target.gatsbyImageData === undefined
          ) {
            throw new Error(
              `Non image asset ${target.contentful_id} in embedded in rich text`,
            )
          }

          const image = () =>
            gatsbyImage ? (
              <MeasurImage
                image={gatsbyImage}
                alt={target.title ?? ''}
                className="richText__img"
              />
            ) : (
              <img src={target.url} loading="lazy" alt={target.title} />
            )

          return target.description !== undefined ? (
            <figure
              className="richText__figure"
              key={`${target.contentful_id}-${target.title ?? ''}}`}
            >
              {image()}
              {target.description && (
                <figcaption className="richText__caption">
                  {target.description}
                </figcaption>
              )}
            </figure>
          ) : (
            image()
          )
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node, _) => {
          if (node.data.target === undefined) {
            return null
          }
          const target = node.data.target as RichTextEntryReference

          if (target.__typename === 'ContentfulTableOfContents') {
            return <TableOfContents section={text} />
          }
          if (target.__typename === 'ContentfulHeading') {
            if (target.level && target.heading) {
              const hash = toDashedSlug(
                target.tableOfContentsHeading ?? target.heading,
              )

              return (
                <target.level
                  className={`richText__heading richText__heading--${target.level} top-24`}
                  id={hash}
                >
                  {target.heading}
                </target.level>
              )
            }
            return null
          }
          if (target.__typename === 'ContentfulTable') {
            return (
              <Table
                section={{
                  ...target,
                  internal: { type: 'ContentfulTable' },
                }}
              />
            )
          }
          if (target.__typename === 'ContentfulLargeLeadForm') {
            if (!pageSlug) {
              throw new Error(
                `Missing page slug for content type ${target.__typename} in rich text`,
              )
            }
            return (
              <LargeLeadForm
                pageSlug={pageSlug}
                section={{
                  ...target,
                  internal: { type: 'ContentfulLargeLeadForm' },
                }}
              />
            )
          }
          if (target.__typename === 'ContentfulSmallLeadForm') {
            if (!pageSlug) {
              throw new Error(
                `Missing page slug for content type ${target.__typename} in rich text`,
              )
            }
            return (
              <SmallLeadForm
                pageSlug={pageSlug}
                section={{
                  ...target,
                  internal: { type: 'ContentfulSmallLeadForm' },
                }}
              />
            )
          }
          if (target.__typename === 'ContentfulLandingSection') {
            if (!products) {
              throw new Error(
                `Missing products for content type ${target.__typename} in rich text`,
              )
            }
            if (!employees) {
              throw new Error(
                `Missing employees for content type ${target.__typename} in rich text`,
              )
            }
            if (!teamMemberPageLinks) {
              throw new Error(
                `Missing teamMemberPageLinks for content type ${target.__typename} in rich text`,
              )
            }
            return (
              <LandingSection
                section={{
                  ...target,
                  internal: { type: 'ContentfulLandingSection' },
                }}
                employees={employees}
                teamMemberPageLinks={teamMemberPageLinks}
              />
            )
          }
          throw new Error(
            `Unsupported entry ${target.contentful_id} embedded in rich text or missing pageslug`,
          )
        },

        [INLINES.HYPERLINK]: (node, children) => {
          if (typeof node.data.uri !== 'string') {
            throw new Error('Node missing hyperlink URL')
          }
          const { uri } = node.data
          const isWebLink = ['http:', 'https:'].includes(new URL(uri).protocol)
          return (
            <a
              className="richText__link"
              href={uri}
              {...(isWebLink ? { rel: 'noreferrer', target: '_blank' } : {})}
            >
              {children}
            </a>
          )
        },
        [INLINES.ENTRY_HYPERLINK]: (node, children) => {
          return <ContentfulLink node={node}>{children}</ContentfulLink>
        },
      },
      renderText: renderFormulas,
    })
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(err)
      return
    }
    Sentry.captureException(err, { extra: { text } })
    return null
  }
}

export const richTextToString = (
  text: RenderRichTextData<
    RichTextAssetReferences | ContentfulRichTextGatsbyReference
  >,
) => renderRichText(text, {})

export const rawRichTextToString = (richTextString: string) => {
  try {
    return richTextString
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        documentToPlainTextString(JSON.parse(richTextString))
      : ''
  } catch (e) {
    return ''
  }
}
